import type {
  AffineInlineEditor,
  AffineTextAttributes,
} from '@blocksuite/affine-shared/types';
import { createLitPortal } from '@blocksuite/affine-components/portal';
import {
  getBlocksuiteLocaleKey,
  type LocaleService,
  translateBlocksuiteText,
} from '@blocksuite/affine-shared/services';
import {
  AFFINE_INLINE_CLIPBOARD_MIME,
  inlineDeltasFromClipboardData,
  insertInlineDeltas,
  sliceInlineDeltas,
  serializeInlineDeltas,
} from '@blocksuite/affine-shared/utils';
import { flip, offset, shift } from '@floating-ui/dom';
import { WithDisposable } from '@blocksuite/global/lit';
import { ShadowlessElement } from '@blocksuite/std';
import {
  type AttributeRenderer,
  InlineEditor,
  type InlineMarkdownMatch,
  type InlineMarkdownMatchContext,
  type InlineRange,
  type InlineRangeProvider,
  type VLine,
} from '@blocksuite/std/inline';
import type { DeltaInsert } from '@blocksuite/store';
import { Text } from '@blocksuite/store';
import { effect, signal } from '@preact/signals-core';
import { css, html, type TemplateResult } from 'lit';
import { property, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import * as Y from 'yjs';
import { z } from 'zod';

import { onVBeforeinput, onVCompositionEnd } from './hooks.js';
import { getPrefixText } from './utils.js';

interface RichTextStackItem {
  meta: Map<'richtext-v-range', InlineRange | null>;
}

const markdownCompletionNames = [
  'bolditalic',
  'bold',
  'italic',
  'strikethrough',
  'underthrough',
  'code',
  'heading-or-quote',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'quote',
  'list',
  'numbered',
  'bulleted',
  'todo',
  'divider',
  'code-block',
  'link',
  'formula',
] as const;

type MarkdownCompletionName = (typeof markdownCompletionNames)[number];

function isMarkdownCompletionName(
  value: string
): value is MarkdownCompletionName {
  return markdownCompletionNames.includes(value as MarkdownCompletionName);
}

function matchesPendingMarkdown(pattern: RegExp, prefixText: string) {
  const flags = pattern.flags.replaceAll('g', '').replaceAll('y', '');
  return new RegExp(pattern.source, flags).test(`${prefixText} `);
}

export class RichText extends WithDisposable(ShadowlessElement) {
  static override styles = css`
    rich-text {
      display: block;
      height: 100%;
      width: 100%;
      overflow-x: auto;
      overflow-y: hidden;

      scroll-margin-top: 50px;
      scroll-margin-bottom: 30px;
    }

    .inline-editor {
      height: 100%;
      width: 100%;
      outline: none;
      cursor: text;
    }

    .inline-editor.readonly {
      cursor: default;
    }

    rich-text .nowrap-lines v-text span,
    rich-text .nowrap-lines v-element span {
      white-space: pre !important;
    }
  `;

  #verticalScrollContainer: HTMLElement | null = null;

  private _markdownCompletionAbortController: AbortController | null = null;

  private _markdownCompletionRequest = 0;

  private readonly _inlineEditor$ = signal<AffineInlineEditor | null>(null);

  private readonly _locale$ = signal<LocaleService | null | undefined>(
    undefined
  );

  private readonly _onCopy = (e: ClipboardEvent) => {
    const inlineEditor = this.inlineEditor;
    if (!inlineEditor) return;

    const inlineRange = inlineEditor.getInlineRange();
    if (!inlineRange) return;

    const text = inlineEditor.yTextString.slice(
      inlineRange.index,
      inlineRange.index + inlineRange.length
    );
    const deltas = sliceInlineDeltas(
      inlineEditor.yText.toDelta() as DeltaInsert<AffineTextAttributes>[],
      inlineRange.index,
      inlineRange.index + inlineRange.length
    );

    e.clipboardData?.setData('text/plain', text);
    e.clipboardData?.setData(
      AFFINE_INLINE_CLIPBOARD_MIME,
      serializeInlineDeltas(deltas as DeltaInsert<AffineTextAttributes>[])
    );
    e.preventDefault();
    e.stopPropagation();
  };

  private readonly _onCut = (e: ClipboardEvent) => {
    const inlineEditor = this.inlineEditor;
    if (!inlineEditor) return;

    const inlineRange = inlineEditor.getInlineRange();
    if (!inlineRange) return;

    const text = inlineEditor.yTextString.slice(
      inlineRange.index,
      inlineRange.index + inlineRange.length
    );
    const deltas = sliceInlineDeltas(
      inlineEditor.yText.toDelta() as DeltaInsert<AffineTextAttributes>[],
      inlineRange.index,
      inlineRange.index + inlineRange.length
    );
    inlineEditor.deleteText(inlineRange);
    inlineEditor.setInlineRange({
      index: inlineRange.index,
      length: 0,
    });

    e.clipboardData?.setData('text/plain', text);
    e.clipboardData?.setData(
      AFFINE_INLINE_CLIPBOARD_MIME,
      serializeInlineDeltas(deltas as DeltaInsert<AffineTextAttributes>[])
    );
    e.preventDefault();
    e.stopPropagation();
  };

  private readonly _onPaste = (e: ClipboardEvent) => {
    const inlineEditor = this.inlineEditor;
    if (!inlineEditor) return;

    const inlineRange = inlineEditor.getInlineRange();
    if (!inlineRange) return;

    const deltas = e.clipboardData
      ? inlineDeltasFromClipboardData(e.clipboardData)
      : [];
    if (deltas.length > 0) {
      insertInlineDeltas(inlineEditor, inlineRange, deltas);
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    const text = e.clipboardData
      ?.getData('text/plain')
      ?.replace(/\r?\n|\r/g, '\n');
    if (!text) return;

    inlineEditor.insertText(inlineRange, text);
    inlineEditor.setInlineRange({
      index: inlineRange.index + text.length,
      length: 0,
    });

    e.preventDefault();
    e.stopPropagation();
  };

  private readonly _onStackItemAdded = (event: {
    stackItem: RichTextStackItem;
  }) => {
    const inlineRange = this.inlineEditor?.getInlineRange();
    if (inlineRange) {
      event.stackItem.meta.set('richtext-v-range', inlineRange);
    }
  };

  private readonly _onStackItemPopped = (event: {
    stackItem: RichTextStackItem;
  }) => {
    const inlineRange = event.stackItem.meta.get('richtext-v-range');
    if (inlineRange && this.inlineEditor?.isValidInlineRange(inlineRange)) {
      this.inlineEditor?.setInlineRange(inlineRange);
    }
  };

  private readonly _clearMarkdownCompletionHint = () => {
    this._markdownCompletionRequest++;
    this._markdownCompletionAbortController?.abort();
    this._markdownCompletionAbortController = null;
  };

  private _getMarkdownCompletionText(name: MarkdownCompletionName) {
    const translate = (key: string, fallback: string) =>
      translateBlocksuiteText(
        this.locale,
        getBlocksuiteLocaleKey('rich-text.markdown-completion', key),
        fallback
      );

    return {
      prefix: translate('prefix', 'Press'),
      separator: translate('separator', 'or'),
      action: translate('action', 'to convert to'),
      target: translate(`target.${name}`, 'formatting'),
      enter: translate('key.enter', 'Enter'),
      space: translate('key.space', 'Space'),
    };
  }

  private _getPendingMarkdownContext(
    inlineEditor: AffineInlineEditor,
    inlineRange: InlineRange,
    prefixText: string,
    pattern: RegExp
  ): InlineMarkdownMatchContext<AffineTextAttributes> {
    return {
      inlineEditor,
      inlineRange,
      prefixText: `${prefixText} `,
      pattern,
      undoManager: this.undoManager,
    };
  }

  private _getMarkdownCompletionName(
    match: InlineMarkdownMatch<AffineTextAttributes>,
    inlineEditor: AffineInlineEditor,
    inlineRange: InlineRange,
    prefixText: string
  ) {
    const completionHint = match.completionHint;
    if (!completionHint) return null;

    const context = this._getPendingMarkdownContext(
      inlineEditor,
      inlineRange,
      prefixText,
      match.pattern
    );
    const name = completionHint.getTarget?.(context) ?? completionHint.type;

    return isMarkdownCompletionName(name) ? name : null;
  }

  private _getPendingMarkdownMatch(
    inlineEditor: AffineInlineEditor,
    inlineRange: InlineRange,
    prefixText: string
  ) {
    return this.markdownMatches.find(match => {
      const { completionHint, pattern } = match;
      if (
        completionHint === undefined ||
        !matchesPendingMarkdown(pattern, prefixText)
      ) {
        return false;
      }

      const context = this._getPendingMarkdownContext(
        inlineEditor,
        inlineRange,
        prefixText,
        pattern
      );
      return completionHint.isEligible?.(context) ?? true;
    });
  }

  private _scheduleMarkdownCompletionHint() {
    const inlineEditor = this.inlineEditor;
    const request = ++this._markdownCompletionRequest;

    this._markdownCompletionAbortController?.abort();
    this._markdownCompletionAbortController = null;

    if (
      !inlineEditor ||
      !this.markdownMatches.length ||
      this.readonly ||
      inlineEditor.isComposing
    ) {
      return;
    }

    const inlineRange = inlineEditor.getInlineRange();
    if (!inlineRange || inlineRange.length !== 0) {
      return;
    }

    const prefixText = getPrefixText(inlineEditor);
    const match = this._getPendingMarkdownMatch(
      inlineEditor,
      inlineRange,
      prefixText
    );
    if (
      !match ||
      !this._getMarkdownCompletionName(
        match,
        inlineEditor,
        inlineRange,
        prefixText
      )
    ) {
      return;
    }

    inlineEditor
      .waitForUpdate()
      .then(() => {
        if (
          request !== this._markdownCompletionRequest ||
          this.readonly ||
          inlineEditor.isComposing
        ) {
          return;
        }

        const currentRange = inlineEditor.getInlineRange();
        if (!currentRange || currentRange.length !== 0) {
          return;
        }

        const currentPrefixText = getPrefixText(inlineEditor);
        const currentMatch = this._getPendingMarkdownMatch(
          inlineEditor,
          currentRange,
          currentPrefixText
        );
        if (!currentMatch) {
          return;
        }

        const name = this._getMarkdownCompletionName(
          currentMatch,
          inlineEditor,
          currentRange,
          currentPrefixText
        );
        if (!name) {
          return;
        }

        const domRange = inlineEditor.toDomRange(currentRange);
        if (!domRange) {
          return;
        }

        const abortController = new AbortController();
        this._markdownCompletionAbortController = abortController;
        const text = this._getMarkdownCompletionText(name);
        const referenceElement = {
          contextElement: inlineEditor.rootElement ?? undefined,
          getBoundingClientRect: () => domRange.getBoundingClientRect(),
          getClientRects: () => domRange.getClientRects(),
        };

        createLitPortal({
          abortController,
          container: document.body,
          shadowDom: true,
          positionStrategy: 'fixed',
          computePosition: {
            referenceElement,
            placement: 'top-start',
            middleware: [offset(6), flip(), shift({ padding: 6 })],
            autoUpdate: true,
          },
          portalStyles: {
            pointerEvents: 'none',
            zIndex: 'var(--affine-z-index-popover)',
          },
          template: html`
            <style>
              .markdown-completion-hint {
                display: flex;
                align-items: center;
                gap: 4px;
                margin-bottom: 6px;
                padding: 4px 6px;
                border: 1px solid
                  color-mix(in srgb, currentColor 12%, transparent);
                border-radius: 4px;
                background: color-mix(
                  in srgb,
                  var(--affine-background-overlay-panel-color, #fff) 88%,
                  transparent
                );
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
                color: var(--affine-text-secondary-color, #77757d);
                font-family: var(--affine-font-sans-family, Inter, sans-serif);
                font-size: 11px;
                line-height: 16px;
                user-select: none;
                white-space: nowrap;
              }
              kbd {
                padding: 0 3px;
                border: 1px solid
                  color-mix(in srgb, currentColor 24%, transparent);
                border-bottom-width: 2px;
                border-radius: 3px;
                background: color-mix(in srgb, currentColor 8%, transparent);
                color: var(--affine-text-primary-color, #141414);
                font-family: inherit;
                font-size: 10px;
                line-height: 14px;
              }
              .markdown-completion-target {
                padding: 0 4px;
                border-radius: 3px;
                background: color-mix(in srgb, currentColor 10%, transparent);
                color: var(--affine-text-primary-color, #141414);
                font-weight: 600;
              }
            </style>
            <div class="markdown-completion-hint" aria-hidden="true">
              <span>${text.prefix}</span>
              <kbd>${text.enter}</kbd>
              <span>${text.separator}</span>
              <kbd>${text.space}</kbd>
              <span>${text.action}</span>
              <span class="markdown-completion-target">${text.target}</span>
            </div>
          `,
        });
      })
      .catch(console.error);
  }

  private get _yText() {
    return this.yText instanceof Text ? this.yText.yText : this.yText;
  }

  // It will listen ctrl+z/ctrl+shift+z and call undoManager.undo/redo, keydown event will not
  get inlineEditor() {
    return this._inlineEditor$.value;
  }

  get inlineEditorContainer() {
    return this._inlineEditorContainer;
  }

  private _init() {
    if (this.inlineEditor) {
      console.error('Inline editor already exists.');
      return;
    }

    if (!this.enableFormat) {
      this.attributesSchema = z.object({});
    }

    // init inline editor
    this._inlineEditor$.value = new InlineEditor<AffineTextAttributes>(
      this._yText,
      {
        isEmbed: delta => this.embedChecker(delta),
        hooks: {
          beforeinput: onVBeforeinput,
          compositionEnd: onVCompositionEnd,
        },
        inlineRangeProvider: this.inlineRangeProvider,
        vLineRenderer: this.vLineRenderer,
      }
    );
    const inlineEditor = this._inlineEditor$.value;
    this._locale$.value = this.locale;
    if (this.attributesSchema) {
      inlineEditor.setAttributeSchema(this.attributesSchema);
    }
    if (this.attributeRenderer) {
      inlineEditor.setAttributeRenderer(this.attributeRenderer);
    }

    const markdownMatches = this.markdownMatches;
    if (markdownMatches) {
      const markdownTransform = (isEnter: boolean = false) => {
        let inlineRange = inlineEditor.getInlineRange();
        if (!inlineRange) return false;

        let prefixText = getPrefixText(inlineEditor);
        if (isEnter) prefixText = `${prefixText} `;

        for (const match of markdownMatches) {
          const { pattern, action } = match;
          if (prefixText.match(pattern)) {
            if (isEnter) {
              inlineEditor.insertText(
                {
                  index: inlineRange.index,
                  length: 0,
                },
                ' '
              );
              inlineEditor.setInlineRange({
                index: inlineRange.index + 1,
                length: 0,
              });
              inlineRange = inlineEditor.getInlineRange();
              if (!inlineRange) return false;
            }

            action({
              inlineEditor,
              prefixText,
              inlineRange,
              pattern,
              undoManager: this.undoManager,
            });
            return true;
          }
        }
        return false;
      };

      inlineEditor.disposables.add(
        inlineEditor.slots.inputting.subscribe(data => {
          if (inlineEditor.isComposing) {
            this._clearMarkdownCompletionHint();
            return;
          }
          if (!inlineEditor.isComposing && data === ' ') {
            this._clearMarkdownCompletionHint();
            markdownTransform();
          }
          queueMicrotask(() => this._scheduleMarkdownCompletionHint());
        })
      );

      inlineEditor.disposables.add(
        inlineEditor.slots.keydown.subscribe(event => {
          if (event.key === 'Escape') {
            this._clearMarkdownCompletionHint();
            return;
          }
          if (event.key === 'Enter') {
            this._clearMarkdownCompletionHint();
          }
          if (event.key === 'Enter' && markdownTransform(true)) {
            event.stopPropagation();
            event.preventDefault();
          }
        })
      );

      inlineEditor.disposables.add(
        effect(() => {
          const inlineRange = inlineEditor.inlineRange$.value;
          if (!inlineRange || inlineRange.length !== 0) {
            this._clearMarkdownCompletionHint();
            return;
          }
          this._scheduleMarkdownCompletionHint();
        })
      );

      inlineEditor.disposables.add(
        effect(() => {
          const locale = this._locale$.value;
          void locale?.language$.value;
          this._scheduleMarkdownCompletionHint();
        })
      );

      inlineEditor.disposables.addFromEvent(
        this,
        'focusout',
        this._clearMarkdownCompletionHint
      );
    }

    // init auto scroll
    inlineEditor.disposables.add(
      effect(() => {
        const inlineRange = inlineEditor.inlineRange$.value;
        if (!inlineRange) return;

        // lazy
        const verticalScrollContainer =
          this.#verticalScrollContainer ||
          (this.#verticalScrollContainer =
            this.verticalScrollContainerGetter?.() || null);

        inlineEditor
          .waitForUpdate()
          .then(() => {
            if (!inlineEditor.mounted || inlineEditor.rendering) return;

            const range = inlineEditor.toDomRange(inlineRange);
            if (!range) return;

            if (verticalScrollContainer) {
              const nativeRange = inlineEditor.getNativeRange();
              if (
                !nativeRange ||
                nativeRange.commonAncestorContainer.parentElement?.contains(
                  inlineEditor.rootElement
                )
              )
                return;

              const containerRect =
                verticalScrollContainer.getBoundingClientRect();
              const rangeRect = range.getBoundingClientRect();

              if (rangeRect.top < containerRect.top) {
                this.scrollIntoView({ block: 'start' });
              } else if (rangeRect.bottom > containerRect.bottom) {
                this.scrollIntoView({ block: 'end' });
              }
            }

            // scroll container is this
            if (this.enableAutoScrollHorizontally) {
              const containerRect = this.getBoundingClientRect();
              const rangeRect = range.getBoundingClientRect();

              let scrollLeft = this.scrollLeft;
              if (
                rangeRect.left + rangeRect.width >
                containerRect.left + containerRect.width
              ) {
                scrollLeft +=
                  rangeRect.left +
                  rangeRect.width -
                  (containerRect.left + containerRect.width) +
                  2;
              }
              this.scrollLeft = scrollLeft;
            }
          })
          .catch(console.error);
      })
    );

    inlineEditor.mount(
      this.inlineEditorContainer,
      this.inlineEventSource,
      this.readonly
    );
  }

  private _unmount() {
    this._clearMarkdownCompletionHint();
    if (this.inlineEditor?.mounted) {
      this.inlineEditor.unmount();
    }
    this._inlineEditor$.value = null;
  }

  override connectedCallback() {
    super.connectedCallback();

    if (!this._yText) {
      console.error('rich-text need yText to init.');
      return;
    }
    if (!this._yText.doc) {
      console.error('yText should be bind to yDoc.');
      return;
    }

    if (!this.undoManager) {
      this.undoManager = new Y.UndoManager(this._yText, {
        trackedOrigins: new Set([this._yText.doc.clientID]),
      });
    }

    if (this.enableUndoRedo) {
      this.disposables.addFromEvent(this, 'keydown', (e: KeyboardEvent) => {
        // oxlint-disable-next-line sonarjs/no-collapsible-if
        if (e.ctrlKey || e.metaKey) {
          if (e.key === 'z' || e.key === 'Z') {
            if (e.shiftKey) {
              this.undoManager.redo();
            } else {
              this.undoManager.undo();
            }
            e.stopPropagation();
          }
        }
      });

      this.undoManager.on('stack-item-added', this._onStackItemAdded);
      this.undoManager.on('stack-item-popped', this._onStackItemPopped);
      this.disposables.add({
        dispose: () => {
          this.undoManager.off('stack-item-added', this._onStackItemAdded);
          this.undoManager.off('stack-item-popped', this._onStackItemPopped);
        },
      });
    }

    if (this.enableClipboard) {
      this.disposables.addFromEvent(this, 'copy', this._onCopy);
      this.disposables.addFromEvent(this, 'cut', this._onCut);
      this.disposables.addFromEvent(this, 'paste', this._onPaste);
    }

    this.updateComplete
      .then(() => {
        this._unmount();
        this._init();

        this.disposables.add({
          dispose: () => {
            this._unmount();
          },
        });
      })
      .catch(console.error);
  }

  override async getUpdateComplete(): Promise<boolean> {
    const result = await super.getUpdateComplete();
    await this.inlineEditor?.waitForUpdate();
    return result;
  }

  // If it is true rich-text will handle undo/redo by itself. (including v-range restore)
  override render() {
    const classes = classMap({
      'inline-editor': true,
      'nowrap-lines': !this.wrapText,
      readonly: this.readonly,
    });

    return html`<div
      contenteditable=${this.readonly ? 'false' : 'true'}
      class=${classes}
    ></div>`;
  }

  override updated(changedProperties: Map<string | number | symbol, unknown>) {
    if (changedProperties.has('locale')) {
      this._locale$.value = this.locale;
    }

    const inlineEditor = this.inlineEditor;
    if (inlineEditor && this._yText && this._yText !== inlineEditor.yText) {
      this._unmount();
      this._init();
      return;
    }
    if (this.inlineEditor && changedProperties.has('readonly')) {
      this.inlineEditor.setReadonly(this.readonly);
      if (this.readonly) {
        this._clearMarkdownCompletionHint();
      }
    }
  }

  @query('.inline-editor')
  private accessor _inlineEditorContainer!: HTMLDivElement;

  @property({ attribute: false })
  accessor attributeRenderer: AttributeRenderer | undefined = undefined;

  @property({ attribute: false })
  accessor attributesSchema: z.ZodSchema | undefined = undefined;

  @property({ attribute: false })
  accessor embedChecker: <
    TextAttributes extends AffineTextAttributes = AffineTextAttributes,
  >(
    delta: DeltaInsert<TextAttributes>
  ) => boolean = () => false;

  @property({ attribute: false })
  accessor enableAutoScrollHorizontally = true;

  // If it is true rich-text will prevent events related to clipboard bubbling up and handle them by itself.
  @property({ attribute: false })
  accessor enableClipboard = true;

  // `attributesSchema` will be overwritten to `z.object({})` if `enableFormat` is false.
  @property({ attribute: false })
  accessor enableFormat = true;

  // bubble up if pressed ctrl+z/ctrl+shift+z.
  @property({ attribute: false })
  accessor enableUndoRedo = true;

  @property({ attribute: false })
  accessor inlineEventSource: HTMLElement | undefined = undefined;

  @property({ attribute: false })
  accessor inlineRangeProvider: InlineRangeProvider | undefined = undefined;

  @property({ attribute: false })
  accessor markdownMatches: InlineMarkdownMatch<AffineTextAttributes>[] = [];

  @property({ attribute: false })
  accessor locale: LocaleService | null | undefined = undefined;

  @property({ attribute: false })
  accessor readonly = false;

  // rich-text will create a undoManager if it is not provided.
  @property({ attribute: false })
  accessor undoManager!: Y.UndoManager;

  @property({ attribute: false })
  accessor verticalScrollContainerGetter:
    | (() => HTMLElement | null)
    | undefined = undefined;

  @property({ attribute: false })
  accessor vLineRenderer: ((vLine: VLine) => TemplateResult) | undefined;

  @property({ attribute: false })
  accessor wrapText = true;

  @property({ attribute: false })
  accessor yText!: Y.Text | Text;
}
